/**
 * One client's customers — the contacts the client keeps and sends to — on
 * that client's page.
 *
 * Read-only here: the client owns its customer list (the web app, the phone
 * sync, its imports), and staff see it rather than edit it. The same list the
 * client sees, from the same server-side list, so searching, filtering, sorting
 * and paging behave exactly as every other register.
 */

import { useMemo } from 'react';
import { Mail, Phone, UserRound } from 'lucide-react';
import {
  ServerTable,
  StatusChip,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  humanize,
  type ServerColumn,
  type Tone,
} from '@connect/web-shared';

export interface CustomerRow {
  public_id: string;
  full_name: string;
  mobile: string | null;
  email: string | null;
  company: string | null;
  city: string | null;
  source: string;
  can_receive: boolean;
  disabled_reason: string | null;
  lists: { public_id: string; name: string }[];
  created_at: string;
  updated_at: string;
}

const RECEIVE_TONES: Record<string, Tone> = { yes: 'success', no: 'neutral' };

export function ClientCustomersTable({
  clientPublicId,
  clientName,
}: {
  clientPublicId: string;
  clientName: string;
}) {
  const endpoint = `/crm/clients/${clientPublicId}/customers`;
  const fetchRows = useMemo(() => createServerFetcher<CustomerRow>(endpoint), [endpoint]);
  const getFacets = useMemo(() => createFacetsFetcher(endpoint), [endpoint]);

  const columns = useMemo<ServerColumn<CustomerRow>[]>(
    () => [
      {
        field: 'full_name',
        header: 'Name',
        icon: <UserRound className="size-3" />,
        filter: 'text',
        minWidth: 180,
        render: (row) => <span className="font-medium text-heading">{row.full_name || '—'}</span>,
      },
      {
        field: 'mobile',
        header: 'Mobile',
        icon: <Phone className="size-3" />,
        filter: 'text',
        width: 140,
        render: (row) => <span className="font-mono text-xs">{row.mobile ?? '—'}</span>,
      },
      {
        field: 'email',
        header: 'Email',
        icon: <Mail className="size-3" />,
        filter: 'text',
        minWidth: 200,
      },
      { field: 'company', header: 'Company', filter: 'text', width: 160, hiddenByDefault: true },
      { field: 'city', header: 'City', filter: 'text', width: 130 },
      {
        field: 'source',
        header: 'Added by',
        filter: 'select',
        width: 120,
        render: (row) => <span className="text-muted">{humanize(row.source)}</span>,
      },
      {
        field: 'can_receive',
        header: 'Receives messages',
        width: 150,
        render: (row) => (
          <StatusChip
            value={row.can_receive ? 'yes' : 'no'}
            tones={RECEIVE_TONES}
            labels={{ yes: 'Yes', no: row.disabled_reason ? humanize(row.disabled_reason) : 'Off' }}
          />
        ),
      },
      {
        field: 'updated_at',
        header: 'Updated',
        filter: 'date',
        width: 130,
        render: (row) => formatDate(row.updated_at),
      },
    ],
    [],
  );

  return (
    <ServerTable<CustomerRow>
      columns={columns}
      fetchRows={fetchRows}
      getFacets={getFacets}
      rowKey={(row) => row.public_id}
      queryKey={['customers', 'client', clientPublicId]}
      stateKey="client.customers"
      searchPlaceholder="Search by name, mobile or email"
      emptyTitle={`No contacts at ${clientName}`}
      emptyDescription="The customers this client keeps appear here as it adds them — from the app, a phone sync or an import."
    />
  );
}
